import React, { useState, useEffect } from 'react';
import { X, AlertCircle} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { isDateTimeInPast } from '../../lib/utils/dateUtils';

const EditSessionModal = ({ isOpen, onClose, session, onUpdate }) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    location: 'Online',
    notes: ''
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && session) {
      // Extract date and time from session_date
      const sessionDate = new Date(session.session_date);
      const date = sessionDate.toISOString().split('T')[0];
      const hours = sessionDate.getHours().toString().padStart(2, '0');
      const minutes = sessionDate.getMinutes().toString().padStart(2, '0');
      const time = `${hours}:${minutes}`;

      setFormData({
        title: session.session_type || '',
        date: date,
        time: time,
        duration: session.duration_minutes || 60,
        location: session.location || 'Online',
        notes: session.notes || ''
      });
    }
  }, [isOpen, session]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setFormError('Session title is required');
      return;
    }
    
    if (!formData.date) {
      setFormError('Session date is required');
      return;
    }
    
    if (!formData.time) {
      setFormError('Session time is required');
      return;
    }
    
    if (isDateTimeInPast(formData.date, formData.time)) {
      setFormError('Session time cannot be in the past');
      return;
    }
    
    setIsSubmitting(true);
    setFormError('');
    
    try {
      // Combine date and time
      const sessionDateTime = new Date(`${formData.date}T${formData.time}`);
      
      // Update the session in the database
      await updateSession(session.id, {
        session_type: formData.title,
        session_date: sessionDateTime.toISOString(),
        duration_minutes: formData.duration,
        location: formData.location,
        notes: formData.notes
      });
      
      onUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating session:', err);
      setFormError('Failed to update session: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to update a session (add this to supabase.js)
  const updateSession = async (sessionId, sessionData) => {
    const { data, error } = await supabase
      .from('sessions')
      .update({
        session_type: sessionData.session_type,
        session_date: sessionData.session_date,
        duration_minutes: sessionData.duration_minutes,
        location: sessionData.location,
        notes: sessionData.notes
      })
      .eq('id', sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-teal-600 px-4 py-3 sm:px-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-white">Edit Session</h3>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {formError && (
              <div className="mb-4 p-2 bg-red-100 text-red-700 rounded flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="e.g., Initial Consultation, Follow-up Meeting"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value, 10)})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                    <option value="120">120 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="Online">Online</option>
                    <option value="Office">Office</option>
                    <option value="Phone">Phone</option>
                    <option value="Client Location">Client Location</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  rows={3}
                  placeholder="Any additional details about this session"
                />
              </div>

              <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:text-sm"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSessionModal;